import {
  useFloating,
  useTransitionStyles,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  Placement,
  FloatingArrow,
  arrow as setArrow,
  FloatingDelayGroup,
  useDelayGroupContext,
  useClick,
  FloatingPortal,
  safePolygon
} from '@floating-ui/react'
import { AnimatePresence, motion, useDragControls, useMotionValue } from 'framer-motion'
import React, { useRef, useState } from 'react'
import { useMeasure, useMedia } from 'react-use'
import styled, { css } from 'styled-components'
import { phoneMaxWidth } from 'utils/media'
import { Portal } from './Portal'

const SHEET_HEIGHT = '100vh'

const TooltipContainer = styled.div<{ $round?: boolean }>`
  border-radius: 8px;
  background-color: white;
  border: 1px solid var(--neutral-20);
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);

  ${(props) =>
    props.$round &&
    css`
      border-radius: 40px;
    `}
`

const TooltipWrapper = styled.div`
  max-width: 390px;
`

const Sheet = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: fixed;
  left: 0;
  bottom: -100vh;
  z-index: 100;
  height: auto;
  width: 100%;
  padding: 0px 24px calc(${SHEET_HEIGHT} + 24px) 24px;
  background-color: white;
  border-radius: 20px 20px 0px 0px;
  touch-action: none;
`

export const Background = styled(motion.div)`
  background-color: black;
  width: 100%;
  height: 100vh;
  opacity: 0.3;
  position: fixed;
  z-index: 50;
  top: 0;
  left: 0;
`

const HandleContainer = styled.div`
  padding: 24px;
  padding-top: 8px;
  cursor: grab;
`
const Handle = styled.div`
  height: 5px;
  width: 48px;
  background-color: var(--neutral-20);
  border-radius: 10px;
`

export const useFloatingPopoverController = () => {
  const [open, setOpen] = useState<boolean>(false)

  return {
    open,
    setOpen
  }
}

export interface FloatingPopoverProps {
  className?: string
  content: React.ReactNode
  children: React.ReactElement
  controls: ReturnType<typeof useFloatingPopoverController>
  transition?: Parameters<typeof useTransitionStyles>['1']
  placement?: Placement
  round?: boolean
  arrow?: boolean
  visible?: boolean
  delay?: { open: number; close: number } | number
  withHover?: boolean
  withClick?: boolean
  onShow?: () => void
  hideOnDesktop?: boolean
}

export const Tooltip = ({
  content,
  children,
  controls,
  placement = 'bottom',
  round,
  arrow,
  visible = true,
  withHover = true,
  withClick = true,
  hideOnDesktop = false,
  onShow,
  className,
  transition
}: FloatingPopoverProps) => {
  const arrowRef = useRef(null)

  const { delay } = useDelayGroupContext()

  const changeVisibility = (open: boolean) => {
    if (open) {
      onShow?.()
    }
    controls.setOpen(open)
  }

  const { refs, floatingStyles, context } = useFloating({
    open: controls.open,
    onOpenChange: changeVisibility,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      setArrow({
        element: arrowRef
      }),
      flip({
        fallbackAxisSideDirection: 'start'
      }),
      shift()
    ]
  })

  const defaultTransition = {
    initial: {
      opacity: 0
    },
    close: {
      opacity: 0
    }
  }

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, transition || defaultTransition)

  const click = useClick(context, { enabled: withClick })
  const hover = useHover(context, { move: false, delay, enabled: withHover, handleClose: safePolygon() })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const interactions = [hover, click, focus, dismiss, role]

  const { getReferenceProps, getFloatingProps } = useInteractions(interactions)

  const childrenWithProps = React.cloneElement(children, { ref: refs.setReference, ...getReferenceProps() })

  return (
    <>
      {childrenWithProps}
      <FloatingPortal>
        {isMounted && visible && (
          <TooltipContainer
            className="Tooltip"
            ref={refs.setFloating}
            style={{ ...transitionStyles, ...floatingStyles }}
            {...getFloatingProps()}
            $round={round}>
            <TooltipWrapper className={className}>
              {arrow && <FloatingArrow fill="#fff" stroke="#E4E4E5" strokeWidth={1} ref={arrowRef} context={context} />}
              {content}
            </TooltipWrapper>
          </TooltipContainer>
        )}
      </FloatingPortal>
    </>
  )
}

export const BottomSheet = ({ controls, content }: Omit<FloatingPopoverProps, 'children'>) => {
  const dragControls = useDragControls()
  const sheetVerticalPosition = useMotionValue(0)
  const [ref, { height }] = useMeasure<HTMLDivElement>()

  const maxDragDown = height / 2

  if (!content) {
    return null
  }

  return (
    <AnimatePresence>
      {controls.open && (
        <Portal>
          <Background onClick={() => controls.setOpen(false)} initial={{ opacity: 0 }} animate={{ opacity: 0.3 }} exit={{ opacity: 0 }} />
          <Sheet
            ref={ref}
            style={{ y: sheetVerticalPosition }}
            initial={{ opacity: 0, translateY: height }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: height }}
            transition={{ translateY: { duration: 0.2, ease: 'easeOut' }, opacity: { duration: 0.2 } }}
            drag="y"
            dragElastic={0.2}
            dragDirectionLock
            dragSnapToOrigin
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: height }}
            onDragEnd={() => {
              if (sheetVerticalPosition.get() > maxDragDown) {
                controls.setOpen(false)
              }
            }}>
            <HandleContainer
              onPointerDown={(event) => {
                dragControls.start(event)
              }}>
              <Handle />
            </HandleContainer>

            {content}
          </Sheet>
        </Portal>
      )}
    </AnimatePresence>
  )
}

export const FloatingPopover = ({ content, children, controls, hideOnDesktop, ...popoverProps }: FloatingPopoverProps) => {
  const isMobile = useMedia(`(max-width: ${phoneMaxWidth}px)`, false)

  if (isMobile) {
    return (
      <>
        {children}
        {content && <BottomSheet controls={controls} content={content} />}
      </>
    )
  } else if (hideOnDesktop) {
    return children
  } else {
    return (
      <FloatingDelayGroup delay={popoverProps.delay || 200}>
        <Tooltip {...popoverProps} controls={controls} content={content}>
          {children}
        </Tooltip>
      </FloatingDelayGroup>
    )
  }
}
